/**
 * Rivals: head-to-head records between one player and everyone they've played against.
 *
 * Computed client-side from the player's plays. In each play, the player is "ahead" of an
 * opponent when they finished in a better position, "behind" when worse, and tied when both
 * share a position. Teammates (same team in that play) are not opponents.
 */
import { PlayOutput } from '../generated-api';

export type MatchResult = 'W' | 'L' | 'D';

export interface Meeting {
  playId: string;
  gameId: string;
  playDate: string;
  result: MatchResult;
}

export interface RivalRecord {
  opponentId: string;
  wins: number;
  losses: number;
  draws: number;
  /** Every play both were in on opposite sides, oldest first. */
  meetings: Meeting[];
}

export interface GameRecord {
  gameId: string;
  /** Oldest first. */
  results: MatchResult[];
  wins: number;
  losses: number;
}

/** Fewest meetings before someone can be your nemesis or favourite target. */
export const MIN_MEETINGS_FOR_HIGHLIGHT = 3;

/** Position index (0 = first) of every team in a play; tied teams share an index. */
const teamPositions = (play: PlayOutput): Record<string, number> => {
  const positions: Record<string, number> = {};
  play.ranking.forEach((entry, index) => {
    const teams = typeof entry === 'string' ? [entry] : entry;
    teams.forEach(team => {
      positions[team] = index;
    });
  });
  return positions;
};

/** Head-to-head record against every opponent, most meetings first. */
export const computeRivalRecords = (playerId: string, plays: PlayOutput[]): RivalRecord[] => {
  const records: Record<string, RivalRecord> = {};
  const oldestFirst = [...plays].sort(
    (a, b) => new Date(a.playDate).getTime() - new Date(b.playDate).getTime()
  );

  for (const play of oldestFirst) {
    const playerTeam = Object.keys(play.teams).find(team => play.teams[team].includes(playerId));
    if (!playerTeam) continue;
    const positions = teamPositions(play);
    const playerPosition = positions[playerTeam];
    if (playerPosition === undefined) continue;

    for (const [team, memberIds] of Object.entries(play.teams)) {
      if (team === playerTeam) continue;
      const opponentPosition = positions[team];
      if (opponentPosition === undefined) continue;
      const result: MatchResult =
        playerPosition < opponentPosition ? 'W' : playerPosition > opponentPosition ? 'L' : 'D';

      for (const opponentId of memberIds) {
        if (opponentId === playerId) continue;
        if (!records[opponentId]) {
          records[opponentId] = { opponentId, wins: 0, losses: 0, draws: 0, meetings: [] };
        }
        const record = records[opponentId];
        if (result === 'W') record.wins++;
        else if (result === 'L') record.losses++;
        else record.draws++;
        record.meetings.push({ playId: play.id, gameId: play.gameId, playDate: play.playDate, result });
      }
    }
  }

  return Object.values(records).sort(
    (a, b) =>
      b.meetings.length - a.meetings.length ||
      b.wins - b.losses - (a.wins - a.losses) ||
      a.opponentId.localeCompare(b.opponentId)
  );
};

const pickHighlight = (
  records: RivalRecord[],
  count: (r: RivalRecord) => number
): RivalRecord | null => {
  const candidates = records.filter(
    r => r.meetings.length >= MIN_MEETINGS_FOR_HIGHLIGHT && count(r) > 0
  );
  if (candidates.length === 0) return null;
  return candidates.reduce((best, r) => {
    const diff = count(r) - count(best);
    if (diff !== 0) return diff > 0 ? r : best;
    // Same count: the more lopsided record wins.
    return count(r) / r.meetings.length > count(best) / best.meetings.length ? r : best;
  });
};

/** The opponent who has finished ahead of you most often. */
export const findNemesis = (records: RivalRecord[]): RivalRecord | null =>
  pickHighlight(records, r => r.losses);

/** The opponent you've finished ahead of most often. */
export const findFavouriteTarget = (records: RivalRecord[]): RivalRecord | null =>
  pickHighlight(records, r => r.wins);

/** Per-game breakdown of a rivalry, most-played game first. */
export const recordByGame = (record: RivalRecord): GameRecord[] => {
  const byGame: Record<string, GameRecord> = {};
  for (const meeting of record.meetings) {
    if (!byGame[meeting.gameId]) {
      byGame[meeting.gameId] = { gameId: meeting.gameId, results: [], wins: 0, losses: 0 };
    }
    const game = byGame[meeting.gameId];
    game.results.push(meeting.result);
    if (meeting.result === 'W') game.wins++;
    if (meeting.result === 'L') game.losses++;
  }
  const lastPlayed = (g: GameRecord) =>
    Math.max(
      ...record.meetings
        .filter(m => m.gameId === g.gameId)
        .map(m => new Date(m.playDate).getTime())
    );
  return Object.values(byGame).sort(
    (a, b) => b.results.length - a.results.length || lastPlayed(b) - lastPlayed(a)
  );
};
